import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { RangeSwitchComponent } from '../../components/shared/range-switch.component';
import { DailyBar, DailyBarsComponent } from '../../components/shared/daily-bars.component';
import { InfoHintComponent } from '../../components/shared/info-hint.component';
import { PersonLinkComponent } from '../../components/shared/person-link.component';
import { EventLinkComponent } from '../../components/shared/event-link.component';
import { UsersService } from '../../services/users-service.service';
import {
  ChatKind,
  ConversationDto,
  ConversationState,
  EngagementOverviewDto,
} from '../../data-transfer-objects/engagement.dto';

/**
 * Chat engagement.
 *
 * Built around one funnel — sent, opened, replied — because each step failing
 * is a different problem: never opened is a notification failure, opened and
 * ignored is a relevance failure, and a reply that never became a conversation
 * is a product failure. A single "messages sent" number hides all three.
 *
 * Turns, not messages, decide whether something is a conversation. Ten messages
 * from one person is a monologue, and counting messages would score it as the
 * best conversation on the board.
 *
 * No message bodies are shown or fetched. The question here is whether two
 * people talked, which needs counts and timestamps only.
 *
 * One component, two tabs. Peer chats are between people, so their funnel is
 * the Chats tab under People; lobby rooms belong to spaces, so theirs is the
 * Lobbies tab under Spaces. The route says which (`data.kind`), the API is
 * asked for that kind only, and the kind switch and the kind column go away
 * because there is nothing left to switch.
 */
@Component({
  selector: 'app-engagement',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatProgressSpinnerModule,
    RangeSwitchComponent,
    DailyBarsComponent,
    InfoHintComponent,
    PersonLinkComponent,
    EventLinkComponent,
  ],
  templateUrl: './engagement.component.html',
  styleUrls: ['./engagement.component.scss'],
})
export class EngagementComponent implements OnInit {
  overview: EngagementOverviewDto | null = null;
  conversations: ConversationDto[] = [];
  loading = true;
  error: string | null = null;
  notice: string | null = null;
  days = 28;
  filter: ConversationState | 'all' = 'all';
  /** Which chats this tab is for; null only if the route did not say. */
  readonly fixedKind: ChatKind | null;
  kind: 'all' | ChatKind;
  backfilling = false;
  backfillProgress: { conversations: number; messages: number } | null = null;

  constructor(private usersService: UsersService, route: ActivatedRoute) {
    const kind = route.snapshot.data['kind'];
    this.fixedKind = kind === 'peer' || kind === 'lobby' ? kind : null;
    this.kind = this.fixedKind ?? 'all';
  }

  get title(): string {
    return { peer: 'Chats', lobby: 'Lobbies' }[this.fixedKind ?? 'peer'] ?? 'Chat engagement';
  }

  get subtitle(): string {
    switch (this.fixedKind) {
      case 'peer':
        return 'Whether one-to-one chats get opened, answered and continued. Metadata only: who, when and how many, never what was said.';
      case 'lobby':
        return "Whether event lobby rooms get opened, answered and continued — one row per space's room. Metadata only: who, when and how many, never what was said.";
      default:
        return 'Whether conversations get opened, answered and continued — peer chats and event lobby rooms, counted separately. Metadata only: who, when and how many, never what was said.';
    }
  }

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.loading = true;
    this.error = null;

    this.usersService.getEngagementOverview(this.days, this.fixedKind ?? undefined).subscribe({
      next: (o) => (this.overview = o),
      error: (err) => (this.error = `Could not load the overview: ${err?.message ?? err}`),
    });

    this.usersService.getConversations(this.days, this.fixedKind ?? undefined).subscribe({
      next: (rows) => {
        this.conversations = rows;
        this.loading = false;
      },
      error: (err) => {
        this.error = `Could not load conversations: ${err?.message ?? err}`;
        this.loading = false;
      },
    });
  }

  setRange(days: number): void {
    this.days = days;
    this.load();
  }

  get rows(): ConversationDto[] {
    return this.conversations.filter(
      (c) =>
        (this.filter === 'all' || c.state === this.filter) &&
        (this.kind === 'all' || c.kind === this.kind)
    );
  }

  /** Reply rate as a percentage, or null when there is nothing to divide by. */
  rate(replied: number, total: number): number | null {
    return total ? Math.round((replied / total) * 100) : null;
  }

  get replyRate(): number | null {
    return this.overview
      ? this.rate(this.overview.totals.replied, this.overview.totals.conversations)
      : null;
  }

  get dailyBars(): DailyBar[] {
    return (this.overview?.daily ?? []).map((d) => ({
      day: d.day,
      value: d.conversations,
      title: `${d.day}: ${d.conversations} started, ${d.messages} messages`,
    }));
  }

  stateLabel(state: ConversationState): string {
    return {
      unopened: 'Never opened',
      'opened-no-reply': 'Opened, no reply',
      replied: 'Replied',
      conversation: 'Conversation',
    }[state];
  }


  /**
   * Rebuild summaries from existing chats, one page at a time.
   *
   * Needed once: conversations that happened before the triggers existed have
   * no summary, and without this the panel would only ever describe the future.
   */
  runBackfill(): void {
    if (this.backfilling) return;
    this.backfilling = true;
    this.backfillProgress = { conversations: 0, messages: 0 };
    this.error = null;

    const step = (): void => {
      this.usersService.backfillEngagement().subscribe({
        next: (result) => {
          this.backfillProgress = {
            conversations: (this.backfillProgress?.conversations ?? 0) + result.processed,
            messages: (this.backfillProgress?.messages ?? 0) + result.messages,
          };
          if (!result.done) {
            step();
            return;
          }
          this.backfilling = false;
          this.notice =
            `Backfilled ${this.backfillProgress.conversations} conversations ` +
            `(${this.backfillProgress.messages} messages).`;
          this.load();
        },
        error: (err) => {
          this.backfilling = false;
          this.error = `Backfill stopped: ${err?.error?.error ?? err?.message ?? err}`;
        },
      });
    };

    step();
  }
}
